<script>
import { onMount, setContext } from 'svelte';
import Router from 'svelte-spa-router';
import { wrap } from 'svelte-spa-router/wrap'
import { contents } from './contents.mjs';
import UserBar from './UserBar.svelte';
import Exercise from './Exercise.svelte';
import NotFound from './NotFound.svelte';
import Contents from './Contents.svelte';
import { load as pyLoad } from './pyeval.mjs';

// Each exercise page can save its text, in this Context variable
setContext('savedText', {});

// The source page should inject some global variables as a Javascript object,
// GLOBALS = { queryroot, username, home_url, logout_url }
// Use GLOBALS.queryroot to fetch all the questions (resolving the "contents" promise)
let globals;
onMount(() => {
	globals = GLOBALS; 
	if (globals?.username == null) document.documentElement.style.setProperty('--navbar-height', '0px');
	const queryroot = globals.root;
	contents.fetch(queryroot);
});

// Initialize the Python interpreter
async function preloadPackages() {
	let c;
	try { c = await contents; } catch (e) {};
	if (c == null) return;
	const imports = [...(c.title.description.imports ?? []), ...(c.rootQuestion.imports ?? [])];
	pyLoad(imports); // or pyLoad(imports, true) to be lazy
}
preloadPackages();

// Set the title, once the contents have loaded
let title = 'SciComp';
async function setTitle() {
	let c;
	try { c = await contents; } catch (e) {};
	if (c == null) return;
	title = c.rootQuestion.title ?? title;
}
setTitle();

// A helper function which, once the contents have loaded, builds up the list of routes
// for navigating between sub-pages
function routes(contents) {
	const routes = {};
	for (const sec of contents.sections) for (const q of sec.questions) {
		routes['/'+q.slug] = wrap({component:Exercise, props:{page:q}});
	}
	routes['/'] = wrap({component:Contents, props:{contents}});
	routes['*'] = wrap({component:NotFound, props:{contents}});
	return routes;
}
</script>

<svelte:head>
<title>{title}</title>
</svelte:head>

<div class="app">
<UserBar user={ globals }/>
{#await contents}
<p>loading questions</p>
{:then contents}
<div>
<Router routes={routes(contents)} />
</div>
{:catch error}
<div class="systemError">
	<h3>Error</h3>
	<div>There was a problem loading the tutorial. Please contact the lecturer.</div>
	<hr/>
	<pre>{ error }</pre>
</div>
{/await}
</div>

<style>
.systemError {
    background-color: rgb(151, 9, 151);
    color: white;
    border: solid black 2pt;
    margin: 4em auto;
	max-width: 40em;
	overflow: auto; 
    padding: 0.5rem;
}
.systemError h3 {
    color: white;
    font-weight: bold;
}
.systemError pre {
    font-size: 80%;
}
.systemError hr {
	border-style: solid;
	border-color: rgb(255,255,255,50%);
	border-width: 1pt 0 0 0;
}


:root {
	--sidebar-w: 30rem;
	--navbar-height: 1.2rem;

	/* easings */
	--out-back:    cubic-bezier(0.175, 0.885, 0.32, 1.275);

	--back:       	#ffffff;
	--back-light: 	#f6fafd;
	--back-api: 		#eff8ff;
	--prime:      	#0099ff;
	--second:     	#676778;
	--third: #aaaac2;
	--flash:      	#40b3ff;
	--heading:	var(--second);
	--text:      		#444;
	--sidebar-text: rgba(255, 255, 255, .75);
	--border-w:  		.3rem; /* border-width  */
	--border-r:  		.3rem; /* border-radius */

	--font: sans-serif;
	--font-mono: monospace;
}

:global(body) {
	padding: 0;
	margin: 0;
	font-family: var(--font);
	font-size: 1rem;
	color: var(--text);
}
:global(html) {
	box-sizing: border-box;
	border-collapse: collapse;
}


:global(h1, h2, h3, h4, h5, h6, blockquote) {
	position: relative;
	margin: 0;
	color: var(--heading);
}

:global(h1, h2) {
	font-family: var(--font);
	line-height: 1.25;
}

:global(h3) { font-weight: 300 }

:global(p, ol, ul) {
	margin: 0 0 1em 0;
}

:global(.b, b, strong) { font-weight: 600 }

:global(button) {
	border: solid var(--second) 0.5pt;
}

:global(code) {
	position: relative;
	border-radius: .3em;
	white-space: nowrap;
	-webkit-font-smoothing: initial;
}

:global(pre code) {
	top: 0;
	white-space: inherit;
	background-color: none;
}

:global(a) {
	position: relative;
	text-decoration: underline;
	cursor: pointer;
	color: inherit;
	user-select: none;
	color: var(--prime);
	transition: color .2s;
}

:global(a:hover, a:active) { 
	color: var(--flash) 
}

:global(a.no-underline) {
	text-decoration: none;
	padding: 0;
}
</style>
